import { useState } from 'react'
import { ClubButton } from './ClubButton'
import { Field } from './Field'

export interface ResolvedPromotion {
  code: string
  summary: string
  free: boolean
  amount_due_cents: number | null
}

interface PromotionCodeFieldProps {
  /** Resolves the code against Stripe, or rejects with a message to show. */
  onApply: (code: string) => Promise<ResolvedPromotion>
  onRemove: () => void
  applied: ResolvedPromotion | null
  disabled?: boolean
}

/**
 * The club's own coupon field. Under hosted Checkout this was Stripe's, which
 * is the single biggest reason the join page could never look like the rest of
 * the club.
 *
 * The code is resolved SERVER-SIDE from the string typed here — this component
 * never sees or sends a promotion-code id, or anyone could paste an id they
 * were never given.
 */
export function PromotionCodeField({ onApply, onRemove, applied, disabled }: PromotionCodeFieldProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [applying, setApplying] = useState(false)

  const apply = async () => {
    if (!code.trim() || applying) return
    setApplying(true)
    setError('')
    try {
      await onApply(code.trim())
      setCode('')
    } catch (caught) {
      // The two messages the server gives are already the ones a member should
      // read, and they are deliberately close: "we don't recognise that code"
      // and "that code is no longer good". Anything else is a fault, not a
      // verdict on the code.
      setError(caught instanceof Error && caught.message ? caught.message : 'We couldn’t check that code')
    } finally {
      setApplying(false)
    }
  }

  if (applied) {
    // Only the code and the way out. What the code is WORTH belongs in the
    // dues breakdown beside it, as a discount line — saying it in both places
    // is the same sentence twice on one card.
    return (
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-t border-brass/25 py-3.5">
        <p className="text-[13.5px] text-ink">
          <span className="font-semibold tracking-[0.02em] text-brass-deep">{applied.code}</span>
          {' applied.'}
        </p>
        <ClubButton variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>
          Remove
        </ClubButton>
      </div>
    )
  }

  return (
    <div className="border-t border-brass/25 py-3.5">
      <Field
        label="Member's code"
        value={code}
        error={error}
        help="If the club gave you one, enter it here."
        onChange={(event) => setCode(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            void apply()
          }
        }}
        disabled={disabled || applying}
      />
      <ClubButton
        variant="ghost"
        size="sm"
        className="mt-3"
        onClick={apply}
        disabled={disabled || applying || !code.trim()}
      >
        {applying ? 'Checking…' : 'Apply'}
      </ClubButton>
    </div>
  )
}
